import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { SEARCH_MAX_LENGTH } from '../../shared/constants';

interface DataTableSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Placeholder text, also used as the field's accessible name. */
  placeholder: string;
  /** Accessible name of the clear (✕) button, shown once there is a value. */
  clearLabel: string;
  /** Max input length. Defaults to the backend cap (255). */
  maxLength?: number;
  /** Disable the field (e.g. nothing to search while the list failed to load). */
  disabled?: boolean;
}

/**
 * Global search field for a datatable toolbar. Size, font size and text/placeholder
 * colour all come from the `theme.searchField` token (250 × 32px, 13.5px, slate 900),
 * with a leading search icon and a trailing clear button that appears once there is a
 * value. Shared by the users and rights-profiles lists so both read identically.
 */
export function DataTableSearchField({
  value,
  onChange,
  placeholder,
  clearLabel,
  maxLength = SEARCH_MAX_LENGTH,
  disabled,
}: DataTableSearchFieldProps) {
  return (
    <TextField
      size="small"
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      sx={{
        width: theme.searchField.width,
        '& .MuiInputBase-root': {
          height: theme.searchField.height,
          pl: '10px',
          pr: '6px',
          borderRadius: theme.borderRadius.xs,
          backgroundColor: theme.palette.background.paper,
          fontSize: theme.searchField.fontSize,
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
        '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: '1px',
        },
        // The toolbar sets a light color; force readable input text.
        '& .MuiInputBase-input': {
          p: 0,
          pl: '8px',
          height: '100%',
          fontSize: theme.searchField.fontSize,
          color: theme.searchField.color,
          WebkitTextFillColor: theme.searchField.color,
          '&::placeholder': { color: theme.searchField.color, opacity: 1 },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <SearchIcon sx={{ fontSize: theme.iconSize.xs, color: theme.palette.text.subtle }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end" sx={{ ml: 0 }}>
              <IconButton
                size="small"
                edge="end"
                aria-label={clearLabel}
                disabled={disabled}
                onClick={() => onChange('')}
                sx={{ p: '3px' }}
              >
                <ClearIcon sx={{ fontSize: theme.iconSize.xs, color: theme.palette.text.subtle }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
        htmlInput: { 'aria-label': placeholder, maxLength },
      }}
    />
  );
}
